import { z } from 'zod';
import { ALL_LIFETIME_FREE_CATEGORIES, CommercialMode } from './enums';
import { PartnerSaleItemSchema, SaleItemSchema } from './assistedSale';
import { BillingCycleSchema, TenantIdSchema } from './schemas';

/**
 * Matriz de transições de tipo comercial (#743, 9.18 da #679 — aprovada pelo
 * owner em 2026-09-20; desenho das duas células com gateway em 2026-09-25).
 *
 * | de \\ para | DIRECT | COURTESY | PARTNER |
 * | --- | --- | --- | --- |
 * | DIRECT | — | `applyLifetimeFree` | agora (sem assinatura paga) ou no fim do período pago |
 * | COURTESY | `removeLifetimeFree` | — | agora |
 * | PARTNER | ao pagar o link (`mode_transition`) | agora | — |
 * | DEMO | recusado | recusado | recusado |
 *
 * DIRECT↔COURTESY já têm callable próprio (F7); a matriz os aponta, não os
 * duplica. DEMO é fixo: os dois tenants demo são compartilhados (D8).
 */
export const CommercialModeTargetSchema = z.enum([CommercialMode.DIRECT, CommercialMode.COURTESY, CommercialMode.PARTNER]);
export type CommercialModeTarget = z.infer<typeof CommercialModeTargetSchema>;

export const ChangeTenantCommercialModeInput = z
  .object({
    tenantId: TenantIdSchema,
    to: CommercialModeTargetSchema,
    reason: z.string().trim().min(3).max(500),
    /** `to: PARTNER` — o parceiro que passa a atender o cliente. */
    partnerId: z.string().min(1).max(128).optional(),
    /** `to: PARTNER` — composição com cobrado e repasse. `to: DIRECT` — o preço negociado. */
    items: z.array(PartnerSaleItemSchema.or(SaleItemSchema)).min(1).max(30).optional(),
    billingCycle: BillingCycleSchema.optional(),
    /** `to: COURTESY` a partir de PARTNER — o motivo da cortesia. */
    courtesyCategory: z.enum(ALL_LIFETIME_FREE_CATEGORIES).exclude(['DEMO']).optional(),
    /** Simula e devolve o que faria, sem gravar nem chamar o gateway. */
    dryRun: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    if (v.to === CommercialMode.PARTNER) {
      if (!v.partnerId) ctx.addIssue({ code: 'custom', path: ['partnerId'], message: 'informe o parceiro' });
      if (!v.items || v.items.some((it) => !('remitCents' in it))) {
        ctx.addIssue({ code: 'custom', path: ['items'], message: 'informe cobrado e repasse de cada item' });
      }
      if (!v.billingCycle) ctx.addIssue({ code: 'custom', path: ['billingCycle'], message: 'informe o ciclo' });
    }
    if (v.to === CommercialMode.DIRECT && (!v.items || !v.billingCycle)) {
      ctx.addIssue({ code: 'custom', path: ['items'], message: 'informe o preço e o ciclo da venda direta' });
    }
  });
export type ChangeTenantCommercialModeInput = z.infer<typeof ChangeTenantCommercialModeInput>;

/**
 * Resultado da troca de tipo comercial (`changeTenantCommercialMode`):
 * - `APPLIED`: o tipo mudou agora;
 * - `SCHEDULED`: DIRECT → PARTNER com assinatura paga — muda no fim do período;
 * - `LINK_CREATED`: PARTNER → DIRECT — muda quando o cliente pagar o link;
 * - `DRY_RUN`: nada foi gravado.
 *
 * Schema nomeado desde a #935 (item 22, 2026-10-13): a tela compara pela
 * constante (`CommercialModeChangeOutcomeSchema.enum.LINK_CREATED`), nunca pelo
 * texto (#761). Antes o enum era inline no output e o admin comparava com
 * `'LINK_CREATED'`.
 */
export const CommercialModeChangeOutcomeSchema = z.enum(['APPLIED', 'SCHEDULED', 'LINK_CREATED', 'DRY_RUN']);
export type CommercialModeChangeOutcome = z.infer<typeof CommercialModeChangeOutcomeSchema>;

export const ChangeTenantCommercialModeOutput = z.object({
  outcome: CommercialModeChangeOutcomeSchema,
  from: z.string(),
  to: CommercialModeTargetSchema,
  effectiveAt: z.number().int().nonnegative().nullable(),
  checkoutUrl: z.url().nullable(),
});
export type ChangeTenantCommercialModeOutput = z.infer<typeof ChangeTenantCommercialModeOutput>;

export const CancelCommercialModeTransitionInput = z.object({
  tenantId: TenantIdSchema,
});
export type CancelCommercialModeTransitionInput = z.infer<typeof CancelCommercialModeTransitionInput>;
